/**
 * `/login/redeem` — confirm page after GET `/api/auth/redeem` peeks a still-valid link
 * (PRD §8, PLAN.md D-15). The raw token arrives in the URL fragment — never sent to the server
 * or in `Referer` — and stays there so a refresh keeps working; it is consumed only when the
 * person clicks Anmelden. Mail scanners that prefetch GET cannot steal the session.
 */

import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { GrantonomyLogo } from "@/components/g-logo";
import { PublicLayout } from "@/components/public-layout";
import { api, ApiError } from "@/lib/api";
import { SESSION_QUERY_KEY, useSession } from "@/lib/session";

function tokenFromHash(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  return value && value.length > 0 ? value : null;
}

const TOKEN_FAIL_REASONS = {
  TOKEN_USED: "used",
  TOKEN_EXPIRED: "expired",
  TOKEN_INVALID: "invalid",
} as const;

export function RedeemPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Still show this page when a session already exists — the link may be for another account.
  useSession();
  const token = tokenFromHash(useLocation().hash);

  const redeem = useMutation({
    mutationFn: (raw: string) => api.post<{ redirectTo: string }>("/auth/redeem", { token: raw }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      navigate(data.redirectTo, { replace: true });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code in TOKEN_FAIL_REASONS) {
        const reason = TOKEN_FAIL_REASONS[error.code as keyof typeof TOKEN_FAIL_REASONS];
        navigate(`/login/link-invalid?reason=${reason}`, { replace: true });
        return;
      }
      toast.error(error instanceof ApiError ? error.localizedMessage : t("errors.INTERNAL_ERROR"));
    },
  });

  if (!token) return <Navigate to="/login/link-invalid?reason=invalid" replace />;

  return (
    <PublicLayout>
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <GrantonomyLogo />
          </div>
          <h1 className="font-display text-4xl">{t("redeem.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("redeem.intro")}</p>

          <div className="mt-6">
            <Button
              data-testid="redeem-submit"
              disabled={redeem.isPending}
              onClick={() => redeem.mutate(token)}
            >
              {redeem.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" /> {t("redeem.submitting")}
                </>
              ) : (
                t("redeem.submit")
              )}
            </Button>
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
